// Pure filter / KPI helpers for the Users page. Kept separate so UsersPage
// itself stays a thin orchestrator.

import type { UsersFilterState } from './components/UsersFilterBar';
import type { UsersKpi } from './components/UsersListPanel';
import { MOCKED_KPIS } from './mockedFields';
import { sortUserRows, type UserRow } from './userViewModel';

function matchesSearch(row: UserRow, q: string): boolean {
  if (!q) return true;
  const hay =
    `${row.name} ${row.email ?? ''} ${row.regions.map((rg) => rg.code).join(' ')}`.toLowerCase();
  return hay.includes(q);
}

/**
 * Whether a row matches the status filter: the three provisioning values match
 * `provisioningStatus`, and `inactive` matches the deactivation flag.
 */
function matchesStatusFilter(row: UserRow, status: UsersFilterState['status']): boolean {
  if (status === 'any') return true;
  if (status === 'inactive') return row.status === 'inactive';
  return row.provisioningStatus === status;
}

function passesFilters(row: UserRow, filters: UsersFilterState, search: string): boolean {
  if (!filters.showInactive && row.status === 'inactive') return false;
  if (!matchesStatusFilter(row, filters.status)) return false;
  // BON-105: multi-role — match if any held role equals the filter.
  if (filters.role !== 'any' && !row.roles.includes(filters.role)) return false;
  if (filters.regionId !== 'any' && !row.regions.some((r) => r.id === filters.regionId)) {
    return false;
  }
  return matchesSearch(row, search);
}

/** Apply the filter bar's state to a list of rows and return them sorted. */
export function filterUserRows(rows: UserRow[], filters: UsersFilterState): UserRow[] {
  const search = filters.search.trim().toLowerCase();
  return sortUserRows(rows.filter((r) => passesFilters(r, filters, search)));
}

export interface UsersKpiSummary {
  kpis: UsersKpi[];
  /** Active imported rows, repeated in the list footer. */
  importedCount: number;
}

/** The four KPI cards for the in-scope row set. */
export function computeUsersKpis(rows: UserRow[]): UsersKpiSummary {
  const total = rows.filter((r) => r.status !== 'inactive').length;
  const invited = rows.filter((r) => r.provisioningStatus === 'invited').length;
  const importedCount = rows.filter((r) => r.status === 'imported').length;
  const kpis: UsersKpi[] = [
    { label: 'Total users', value: total, sub: 'across all roles' },
    { label: 'Pending invites', value: invited, sub: 'sent · awaiting sign-in' },
    {
      label: 'Imported (no login)',
      value: importedCount,
      sub: 'needs invite',
      warn: importedCount > 0,
    },
    {
      label: 'Active sessions',
      value: MOCKED_KPIS.activeSessions,
      sub: 'in the last hour',
      mockedPending: 'UserSession aggregate (last-hour bucket)',
    },
  ];
  return { kpis, importedCount };
}
